import { useEffect, useState } from 'react';

import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';

/**
 * Shared geometry for pages that claim a slideout kind as an INLINE HOST
 * (`registerInlineHost`) and dock the panel beside their content instead of
 * letting the global overlay render it — the action detail page's entity
 * panel and the chat page's docked proposal pane. One definition of the
 * eligibility math, so the two pages can never disagree about when a docked
 * panel fits.
 */

/**
 * Width reserved for the app's nav sidebar when deciding whether the page
 * content keeps enough room beside a docked panel.
 */
const NAV_SIDEBAR_ALLOWANCE = 224;

/**
 * Resize bounds for the docked/inline panel, in px — the same range whether
 * the panel is resized by the action page's `SplitView` handle or the chat
 * page's drag hook (`useProposalPaneResize`).
 */
export const INLINE_PANEL_MIN_WIDTH = 400;
export const INLINE_PANEL_MAX_WIDTH = 900;

/**
 * True while the viewport has room to dock a panel of the shared slideout
 * width beside at least `minContentWidth` of page content (past the nav
 * sidebar), on a large screen. Tracks window resizes live, so the claim is
 * dropped — and the global overlay takes over — the moment the room is gone.
 *
 * Callers `&&` their own page-specific conditions (feature gates, view
 * modes) on top; this hook owns only the geometry.
 */
export function useCanHostInlinePanel(minContentWidth: number): boolean {
  const isLargeScreen = useBreakpoint('lg');
  const { width: panelWidth } = useSlideoutWidth();

  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  return (
    isLargeScreen &&
    viewportWidth - NAV_SIDEBAR_ALLOWANCE - panelWidth >= minContentWidth
  );
}
